import React from "react";
import { Table, Pagination } from "@heroui/react";
import { getPageNumbers } from "../lib/pagination";

export interface DataTableColumn {
  key: string;
  header: React.ReactNode;
  className?: string;
  isRowHeader?: boolean;
}

export interface DataTablePaginationConfig {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  itemLabel: string;
}

interface DataTableProps<T> {
  ariaLabel: string;
  columns: DataTableColumn[];
  rows: T[];
  getRowId: (row: T) => string;
  renderRow: (row: T) => React.ReactNode[];
  className?: string;
  isLoading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: React.ReactNode;
  onRowAction?: (key: React.Key) => void;
  pagination?: DataTablePaginationConfig;
}

// The shared table shell used across the affiliate dashboard: header/body
// chrome, loading skeleton rows, an empty state, and an optional
// Pagination footer, all driven off `columns` so the colSpan used for the
// loading/empty rows always matches the real column count. Callers only
// write the per-column header defs and a renderRow function for the cell
// content; pagination is opt-in via the `pagination` prop rather than each
// caller hand-rolling the same Pagination.Summary/Content/ellipsis block.
// Mirrors admin-dashboard's components/DataTable.tsx.
export function DataTable<T>({
  ariaLabel,
  columns,
  rows,
  getRowId,
  renderRow,
  className,
  isLoading,
  loadingRowCount = 3,
  emptyMessage = "No data yet",
  onRowAction,
  pagination,
}: DataTableProps<T>) {
  const colCount = columns.length;

  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label={ariaLabel} className={className} onRowAction={onRowAction}>
          <Table.Header>
            {columns.map((col) => (
              <Table.Column key={col.key} isRowHeader={col.isRowHeader} className={col.className}>
                {col.header}
              </Table.Column>
            ))}
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              Array.from({ length: loadingRowCount }).map((_, i) => (
                <Table.Row key={`loading-${i}`}>
                  <Table.Cell colSpan={colCount}>
                    <div className="h-5 w-full animate-pulse rounded bg-default/40" />
                  </Table.Cell>
                </Table.Row>
              ))
            ) : rows.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={colCount} className="text-center text-muted py-8">
                  {emptyMessage}
                </Table.Cell>
              </Table.Row>
            ) : (
              rows.map((row) => {
                const id = getRowId(row);
                return (
                  <Table.Row key={id} id={id} className={onRowAction ? "cursor-pointer" : undefined}>
                    {renderRow(row).map((cell, i) => (
                      <Table.Cell key={columns[i]?.key ?? i} className={columns[i]?.className}>
                        {cell}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
      {pagination && pagination.total > 0 && pagination.pageCount > 1 && (
        <Table.Footer>
          <Pagination size="sm">
            <Pagination.Summary>
              {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}{" "}
              {pagination.itemLabel}
            </Pagination.Summary>
            <Pagination.Content>
              <Pagination.Item>
                <Pagination.Previous
                  isDisabled={pagination.page === 1}
                  onPress={() => pagination.onPageChange(Math.max(1, pagination.page - 1))}
                >
                  <Pagination.PreviousIcon />
                  Prev
                </Pagination.Previous>
              </Pagination.Item>
              {getPageNumbers(pagination.page, pagination.pageCount).map((p, i) =>
                p === "ellipsis" ? (
                  <Pagination.Item key={`ellipsis-${i}`}>
                    <Pagination.Ellipsis />
                  </Pagination.Item>
                ) : (
                  <Pagination.Item key={p}>
                    <Pagination.Link
                      isActive={p === pagination.page}
                      onPress={() => pagination.onPageChange(p)}
                    >
                      {p}
                    </Pagination.Link>
                  </Pagination.Item>
                )
              )}
              <Pagination.Item>
                <Pagination.Next
                  isDisabled={pagination.page === pagination.pageCount}
                  onPress={() => pagination.onPageChange(Math.min(pagination.pageCount, pagination.page + 1))}
                >
                  Next
                  <Pagination.NextIcon />
                </Pagination.Next>
              </Pagination.Item>
            </Pagination.Content>
          </Pagination>
        </Table.Footer>
      )}
    </Table>
  );
}
